<template>
  <div class="mt-8 flex min-h-[80vh] justify-center">
    <div class="w-full max-w-lg">
      <div class="mb-6 text-center">
        <h1>Advanced Settings</h1>
      </div>

      <div class="card bg-base-100 border border-base-300 p-6 mb-4">
        <h2 class="text-lg font-semibold mb-1">Cache Management</h2>
        <p class="text-sm text-gray-500 mb-4">
          Flushes all server-side caches and, in the background, rebuilds the
          <code>PolicyDetailSnapshot</code>, <code>PolicyExplorerCache</code>,
          and <code>AccountSearchIndex</code>. The rebuilds run after the
          request returns and can take several minutes.
        </p>

        <div class="mb-2 flex items-baseline justify-between gap-4">
          <span class="text-xs font-medium tracking-wider text-gray-500 uppercase">Rebuild queues</span>
          <div class="text-xs text-gray-500">
            <button
              type="button"
              class="cursor-pointer hover:underline disabled:cursor-default disabled:no-underline"
              :disabled="statsLoading"
              @click="loadStats">
              {{ statsLoading ? 'Checking…' : 'Refresh' }}
            </button>
            <span v-if="statsError"> · check failed</span>
            <span v-else-if="checkedAt"> · checked {{ checkedAt }}</span>
          </div>
        </div>

        <div
          v-if="!stats && statsLoading"
          class="mb-4 grid grid-cols-2 gap-3">
          <Skeleton
            v-for="i in 2"
            :key="i"
            shape="rectangle"
            width="100%"
            height="164px" />
        </div>

        <div
          v-else-if="!stats"
          class="mb-4 rounded-sm border border-base-300 p-4 text-sm text-gray-500">
          Couldn’t load queue stats.
        </div>

        <div
          v-else
          class="mb-4 grid grid-cols-2 gap-3">
          <div class="rounded-sm border border-base-300 p-4">
            <div class="mb-2 flex items-center gap-2">
              <span
                class="h-2 w-2 shrink-0 rounded-full"
                :class="dotClass(searchIndexStatus.tone)"></span>
              <span class="font-mono text-sm">AccountSearchIndex</span>
            </div>

            <div
              class="mb-4 text-lg"
              :class="textClass(searchIndexStatus.tone)">
              {{ searchIndexStatus.label }}
            </div>

            <div class="grid grid-cols-2 gap-y-3 text-sm">
              <div>
                <div class="text-xs text-gray-500">Pending</div>
                <div class="font-mono">{{ stats.accountSearchIndex.pending.toLocaleString() }}</div>
              </div>
              <div>
                <div class="text-xs text-gray-500">Backlog age</div>
                <div class="font-mono">{{ formatQueueDuration(stats.accountSearchIndex.backlogAgeSeconds) }}</div>
              </div>
              <div class="col-span-2">
                <div class="text-xs text-gray-500">Oldest pending</div>
                <div class="font-mono">{{ formatQueueTimestamp(stats.accountSearchIndex.oldestPendingAt) }}</div>
              </div>
            </div>
          </div>

          <div class="flex flex-col rounded-sm border border-base-300 p-4">
            <div class="mb-2 flex items-center gap-2">
              <span
                class="h-2 w-2 shrink-0 rounded-full"
                :class="dotClass(explorerCacheStatus.tone)"></span>
              <span class="font-mono text-sm">PolicyExplorerCache</span>
            </div>

            <div
              class="mb-4 text-lg"
              :class="textClass(explorerCacheStatus.tone)">
              {{ explorerCacheStatus.label }}
            </div>

            <div class="grid grid-cols-2 gap-y-3 text-sm">
              <div>
                <div class="text-xs text-gray-500">Pending</div>
                <div class="font-mono">{{ stats.policyExplorerCache.pending.toLocaleString() }}</div>
              </div>
              <div>
                <div class="text-xs text-gray-500">Backlog age</div>
                <div class="font-mono">{{ formatQueueDuration(stats.policyExplorerCache.backlogAgeSeconds) }}</div>
              </div>
              <div>
                <div class="text-xs text-gray-500">Processing</div>
                <div class="font-mono">{{ stats.policyExplorerCache.processing.toLocaleString() }}</div>
              </div>
              <div>
                <div class="text-xs text-gray-500">Stuck</div>
                <div
                  class="font-mono"
                  :class="{ 'text-red-600': stats.policyExplorerCache.stuck > 0 }">
                  {{ stats.policyExplorerCache.stuck.toLocaleString() }}
                </div>
              </div>
            </div>

            <div class="mt-auto flex flex-wrap items-center justify-between gap-2 pt-4 text-xs text-gray-500">
              <span>~{{ stats.policyExplorerCache.completedRetainedApprox.toLocaleString() }} retained</span>
              <span
                class="rounded-sm border px-2 py-0.5"
                :class="
                  stats.policyExplorerCache.fullRebuildPending ? 'border-amber-400 text-amber-600' : 'border-base-300 text-gray-500'
                ">
                {{ stats.policyExplorerCache.fullRebuildPending ? 'Full rebuild queued' : 'No full rebuild queued' }}
              </span>
            </div>
          </div>
        </div>

        <AppButton primary class="w-full" size="md" :disabled="flushing" @click="flush">
          {{ flushing ? 'Flushing…' : 'Flush All' }}
        </AppButton>
      </div>

      <div v-if="isDev" class="card bg-base-100 border border-base-300 p-6">
        <h2 class="text-lg font-semibold mb-1">Seed Servicing Role Mappings</h2>
        <p class="text-sm text-gray-500 mt-1 mb-4">This tool is only available in the Development environment.</p>
        <p class="text-sm text-gray-500 mb-4">
          Creates dummy <code>UserServicingRoleMapping</code> records for known dev users across a
          sample of active policies and programs. Safe to run multiple times — existing records are
          updated, not duplicated.
        </p>
        <AppButton primary :disabled="seeding" @click="seedMappings">
          {{ seeding ? 'Seeding…' : 'Run Seed' }}
        </AppButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import AppButton from '@/components/v2/AppButton.vue';
import Skeleton from '@/components/v2/Skeleton.vue';
import { adminService } from '@/services/AdminService';
import { cacheService, type QueueStatsResponse } from '@/services/api';
import { useToast } from '@/composables/useToast';
import { DateTime } from 'luxon';
import { formatQueueDuration, formatQueueTimestamp, queueStatus, type QueueStatus, type QueueTone } from './AdminSettings';

const DOT_CLASSES: Record<QueueTone, string> = {
  ok: 'bg-green-600',
  warn: 'bg-amber-500',
  error: 'bg-red-600',
};

const TEXT_CLASSES: Record<QueueTone, string> = {
  ok: 'text-green-700',
  warn: 'text-amber-600',
  error: 'text-red-600',
};

export default {
  name: 'AdminSettings',
  components: { AppButton, Skeleton },
  setup() {
    return { toast: useToast() };
  },
  data: () => ({
    seeding: false,
    flushing: false,
    stats: undefined as QueueStatsResponse | undefined,
    statsLoading: false,
    statsError: false,
    checkedAt: '',
    isDev: import.meta.env.DEV || import.meta.env.VITE_SENTRY_ENVIRONMENT === 'Development',
  }),
  computed: {
    searchIndexStatus(): QueueStatus {
      return queueStatus(this.stats?.accountSearchIndex ?? { pending: 0, backlogAgeSeconds: 0 });
    },
    explorerCacheStatus(): QueueStatus {
      return queueStatus(this.stats?.policyExplorerCache ?? { pending: 0, backlogAgeSeconds: 0 });
    },
  },
  methods: {
    formatQueueDuration,
    formatQueueTimestamp,
    dotClass(tone: QueueTone): string {
      return DOT_CLASSES[tone];
    },
    textClass(tone: QueueTone): string {
      return TEXT_CLASSES[tone];
    },
    async loadStats() {
      this.statsLoading = true;
      try {
        this.stats = await cacheService.queueStats();
        this.statsError = false;
        this.checkedAt = DateTime.now().toFormat('hh:mm a ZZZZ');
      } catch {
        // Keep the last good reading on screen — a failed refresh shouldn't blank the card.
        this.statsError = true;
      } finally {
        this.statsLoading = false;
      }
    },
    async seedMappings() {
      this.seeding = true;
      try {
        const result = await adminService.seedDevUserServicingRoleMappings();
        this.toast.success(`Seeded ${result.count} UserServicingRoleMapping record(s).`);
      } catch {
        this.toast.error('Failed to seed records. Check the console for details.');
      } finally {
        this.seeding = false;
      }
    },
    async flush() {
      this.flushing = true;
      try {
        await cacheService.flush();
        this.toast.success('Cache flushed successfully.');
        // The flush queues the rebuilds, so the queues read differently straight after.
        await this.loadStats();
      } catch {
        this.toast.error('Failed to flush cache. Check the console for details.');
      } finally {
        this.flushing = false;
      }
    },
  },
  async mounted() {
    await this.loadStats();
  },
};
</script>
